'use client';

import {
  type ComponentPropsWithoutRef,
  createContext,
  type Ref,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

import {
  type InstantHoverHighlightState,
  type InstantHoverItemHandlers,
  useInstantHoverHighlight,
} from '@/hooks/useInstantHoverHighlight';
import { cn } from '@/lib/utils';

type TableHighlightContextValue = {
  registerRow: () => number;
  getRowState: (id: number) => InstantHoverHighlightState;
  getRowHandlers: (
    id: number,
    handlers?: InstantHoverItemHandlers<HTMLTableRowElement>
  ) => InstantHoverItemHandlers<HTMLTableRowElement>;
};

const TableHighlightContext = createContext<TableHighlightContextValue | null>(null);

type AssignableReference<T> = { current: T | null };

const isAssignableReference = <T,>(reference: Ref<T> | null | undefined): reference is AssignableReference<T> =>
  typeof reference === 'object' && reference !== null && 'current' in reference;

function composeReferences<T>(...references: Array<Ref<T> | null | undefined>) {
  return (value: T | null) => {
    for (const reference of references) {
      if (!reference) continue;
      if (typeof reference === 'function') {
        reference(value);
      } else if (isAssignableReference(reference)) {
        reference.current = value;
      }
    }
  };
}

type WithRefProperty<Element extends HTMLElement, Properties> = Properties & { ref?: Ref<Element> };

type TableProperties = WithRefProperty<HTMLTableElement, ComponentPropsWithoutRef<'table'>>;

const Table = ({ className, ref: forwardedRef, ...properties }: TableProperties) => (
  <div data-slot="table-container" className="relative w-full overflow-x-auto">
    <table
      ref={forwardedRef}
      data-slot="table"
      className={cn('w-full caption-bottom text-sm', className)}
      {...properties}
    />
  </div>
);
Table.displayName = 'Table';

type TableHeaderProperties = WithRefProperty<HTMLTableSectionElement, ComponentPropsWithoutRef<'thead'>>;

const TableHeader = ({ className, ref: forwardedRef, ...properties }: TableHeaderProperties) => (
  <thead
    ref={forwardedRef}
    data-slot="table-header"
    className={cn('[&_tr]:border-b [&_tr]:border-border/60', className)}
    {...properties}
  />
);
TableHeader.displayName = 'TableHeader';

type TableBodyProperties = WithRefProperty<HTMLTableSectionElement, ComponentPropsWithoutRef<'tbody'>>;

const TableBody = ({ className, ref: forwardedRef, ...properties }: TableBodyProperties) => {
  const highlight = useInstantHoverHighlight<HTMLTableSectionElement, HTMLTableRowElement>({ fadeDurationMs: 200 });
  const rowIdCounterReference = useRef(0);

  rowIdCounterReference.current = 0;

  const registerRow = useCallback(() => {
    const id = rowIdCounterReference.current;
    rowIdCounterReference.current += 1;
    return id;
  }, []);

  const contextValue = useMemo<TableHighlightContextValue>(
    () => ({
      registerRow,
      getRowState: (id) => highlight.getItemState(id),
      getRowHandlers: (id, handlers) => highlight.getItemProps(id, handlers),
    }),
    [highlight, registerRow]
  );

  const { ref: highlightReference, ...containerHandlers } = highlight.containerProps;

  return (
    <TableHighlightContext.Provider value={contextValue}>
      <tbody
        ref={composeReferences(forwardedRef, highlightReference)}
        data-slot="table-body"
        className={cn('[&_tr:last-child]:border-0', className)}
        {...containerHandlers}
        {...properties}
      />
    </TableHighlightContext.Provider>
  );
};
TableBody.displayName = 'TableBody';

type TableRowProperties = WithRefProperty<HTMLTableRowElement, ComponentPropsWithoutRef<'tr'>>;

const TableRow = ({
  className,
  onPointerEnter,
  onFocusCapture,
  ref: forwardedRef,
  ...properties
}: TableRowProperties) => {
  const highlightContext = useContext(TableHighlightContext);
  const rowIdReference = useRef<number | null>(null);

  let highlightState: InstantHoverHighlightState | null = null;
  let handlers: InstantHoverItemHandlers<HTMLTableRowElement> | undefined;

  const handlerOverrides: InstantHoverItemHandlers<HTMLTableRowElement> | undefined =
    onPointerEnter || onFocusCapture
      ? {
          ...(onPointerEnter ? { onPointerEnter } : {}),
          ...(onFocusCapture ? { onFocusCapture } : {}),
        }
      : undefined;

  if (highlightContext) {
    if (rowIdReference.current === null) {
      rowIdReference.current = highlightContext.registerRow();
    }

    highlightState = highlightContext.getRowState(rowIdReference.current);
    handlers = highlightContext.getRowHandlers(rowIdReference.current, handlerOverrides);
  }

  const fallbackHoverClass = highlightContext ? undefined : 'hover:bg-muted/10 dark:hover:bg-muted/20';
  const highlightClass = highlightState?.isHighlighted ? 'bg-muted/12 dark:bg-muted/20' : undefined;

  const pointerEnterHandler = handlers?.onPointerEnter ?? onPointerEnter;
  const focusCaptureHandler = handlers?.onFocusCapture ?? onFocusCapture;

  return (
    <tr
      ref={forwardedRef}
      data-slot="table-row"
      className={cn(
        'border-b border-border/60 data-[state=selected]:bg-muted/12 dark:data-[state=selected]:bg-muted/20',
        fallbackHoverClass,
        highlightClass,
        className
      )}
      onPointerEnter={pointerEnterHandler}
      onFocusCapture={focusCaptureHandler}
      {...properties}
    />
  );
};
TableRow.displayName = 'TableRow';

type TableHeadProperties = WithRefProperty<HTMLTableCellElement, ComponentPropsWithoutRef<'th'>>;

const TableHead = ({ className, ref: forwardedRef, ...properties }: TableHeadProperties) => (
  <th
    ref={forwardedRef}
    data-slot="table-head"
    className={cn(
      'h-9 whitespace-nowrap px-3 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...properties}
  />
);
TableHead.displayName = 'TableHead';

type TableCellProperties = WithRefProperty<HTMLTableCellElement, ComponentPropsWithoutRef<'td'>>;

const TableCell = ({ className, ref: forwardedRef, ...properties }: TableCellProperties) => (
  <td
    ref={forwardedRef}
    data-slot="table-cell"
    className={cn(
      'whitespace-nowrap px-3 py-3 text-sm text-foreground/90 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...properties}
  />
);
TableCell.displayName = 'TableCell';

export { Table, TableHeader, TableBody, TableHead, TableRow, TableCell };
